import { Routes, Route, Navigate } from "react-router-dom";
import { Box } from "@mui/material";
import { lazy, useEffect, useRef } from "react";
import { useStore } from "@/contexts/StoreContext";
import { observer } from "mobx-react-lite";
import Loading from "@/pages/Loading";
import HomeRedirect from "@/pages/HomeRedirect";
import ProjectPage from "@/pages/ProjectPage";
import ProjectTabRedirect from "@/pages/ProjectTabRedirect";
import { useMetadata, useUserInfo } from "@/api/api";
import { toastError } from "@/utils/toast";
import { AppRoute, AppRoutePattern } from "@/types/routes";
import { ErrorBoundary } from "@/components/ui/ErrorBoundary";

const testRouteComponents = import.meta.env.DEV
  ? {
      TestPage: lazy(() => import("@/pages/tests/TestPage")),
      TestVideo: lazy(() => import("@/pages/tests/TestVideo")),
      TestVideoAnalysis: lazy(() => import("@/pages/tests/TestVideoAnalysis")),
      TestVideoExtend: lazy(() => import("@/pages/tests/TestVideoExtend")),
      TestVideoCut: lazy(() => import("@/pages/tests/TestVideoCut")),
      TestCharacter: lazy(() => import("@/pages/tests/TestCharacter")),
      TestStyleDnaPage: lazy(() => import("@/pages/tests/TestStyleDnaPage"))
    }
  : null;

const App = () => {
  const store = useStore();
  const { metaDataStore, actionStore, userStore } = store;

  useEffect(() => {
    void metaDataStore.makePersist();
    void actionStore.hydrate();
  }, [metaDataStore, actionStore]);

  /** After hydration, always refetch metadata so lists (e.g. video_models) stay in sync with the API; persisted cache is stale-while-revalidate. */
  const getMetaData = useMetadata(metaDataStore.isHydrated);
  const userInfoQuery = useUserInfo();
  const hasShownMetadataErrorRef = useRef(false);

  useEffect(() => {
    if (getMetaData.data) {
      metaDataStore.setMetadata(getMetaData.data);
    }
  }, [getMetaData.data, metaDataStore]);

  useEffect(() => {
    if (userInfoQuery.data) {
      userStore.setUserInfo(userInfoQuery.data);
    }
  }, [userInfoQuery.data, userStore]);

  useEffect(() => {
    if (getMetaData.isError && !hasShownMetadataErrorRef.current) {
      hasShownMetadataErrorRef.current = true;
      toastError("Something went wrong with fetching metadata");
    }

    if (getMetaData.data) {
      hasShownMetadataErrorRef.current = false;
    }
  }, [getMetaData.data, getMetaData.isError]);

  if (!actionStore.isHydrated || !metaDataStore.isHydrated) {
    return <Loading />;
  }

  return (
    <Box sx={{ width: "100vw", height: "100vh", overflow: "hidden" }}>
      <ErrorBoundary>
        <Routes>
          <Route path={AppRoutePattern.Home} element={<HomeRedirect />} />
          <Route path={AppRoutePattern.ProjectPanel} element={<ProjectPage />} />
          <Route path={AppRoutePattern.ProjectRoot} element={<ProjectTabRedirect />} />
          {testRouteComponents ? (
            <>
              <Route path={AppRoutePattern.Test} element={<testRouteComponents.TestPage />} />
              <Route path={AppRoutePattern.TestVideo} element={<testRouteComponents.TestVideo />} />
              <Route path={AppRoutePattern.TestVideoAnalysis} element={<testRouteComponents.TestVideoAnalysis />} />
              <Route path={AppRoutePattern.TestVideoExtend} element={<testRouteComponents.TestVideoExtend />} />
              <Route path={AppRoutePattern.TestVideoCut} element={<testRouteComponents.TestVideoCut />} />
              <Route path={AppRoutePattern.TestCharacter} element={<testRouteComponents.TestCharacter />} />
              <Route path={AppRoutePattern.TestStyleDna} element={<testRouteComponents.TestStyleDnaPage />} />
            </>
          ) : null}
          <Route path="*" element={<Navigate to={AppRoute.Home} replace />} />
        </Routes>
      </ErrorBoundary>
    </Box>
  );
};

export default observer(App);
